import getRouterBasename from '@/lib/router';
import { Navigate, createBrowserRouter } from 'react-router-dom';

import AppLayout from '@/components/AppLayout';
import AuthCallback from 'pages/AuthCallback';
import ChatPage from 'pages/ChatPage';
import Element from 'pages/Element';
import Env from 'pages/Env';
import Login from 'pages/Login';
import Thread from 'pages/Thread';
import KnowledgeBaseList from 'pages/KnowledgeBase/index';
import KnowledgeBaseCreate from 'pages/KnowledgeBase/Create';
import KnowledgeBaseDetail from 'pages/KnowledgeBase/Detail';
import AdminPage from 'pages/Admin/index';
import OrganizationsPage from 'pages/Admin/Organizations';
import SystemPromptsPage from 'pages/Admin/SystemPrompts';
import ModelManagementPage from 'pages/Admin/ModelManagement';
import ExportPage from 'pages/Export/index';
import { useAppConfig } from '@/hooks/useAppConfig';

function withAppLayout(element: React.ReactNode) {
  return <AppLayout>{element}</AppLayout>;
}

/**
 * 單機版沒有帳號與業者的概念，側欄雖已隱藏這兩頁，直接輸入網址仍會進得來，
 * 故在路由層一併導回聊天頁。設定尚未載入完成時先不算數（回傳 null），
 * 避免線上版出現一瞬間的誤導轉。
 */
function HideInSingleUserMode({ children }: { children: React.ReactNode }) {
  const config = useAppConfig();
  if (config === null) return null;
  if (config.single_user_mode) return <Navigate replace to="/chat" />;
  return <>{children}</>;
}

/**
 * HideInSingleUserMode 的反向：只有單機版看得到（模型管理頁——線上版依決策
 * D4 先整頁隱藏,API 仍在但前端不提供入口;直接輸入網址也導回聊天頁）。
 */
function OnlyInSingleUserMode({ children }: { children: React.ReactNode }) {
  const config = useAppConfig();
  if (config === null) return null;
  if (!config.single_user_mode) return <Navigate replace to="/chat" />;
  return <>{children}</>;
}

export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: withAppLayout(<ChatPage />)
    },
    {
      path: '/chat',
      element: withAppLayout(<ChatPage />)
    },
    {
      path: '/knowledge-base',
      element: withAppLayout(<KnowledgeBaseList />)
    },
    {
      path: '/knowledge-base/create',
      element: withAppLayout(<KnowledgeBaseCreate />)
    },
    {
      path: '/knowledge-base/:id',
      element: withAppLayout(<KnowledgeBaseDetail />)
    },
    {
      path: '/admin',
      element: withAppLayout(
        <HideInSingleUserMode>
          <AdminPage />
        </HideInSingleUserMode>
      )
    },
    {
      path: '/admin/organizations',
      element: withAppLayout(
        <HideInSingleUserMode>
          <OrganizationsPage />
        </HideInSingleUserMode>
      )
    },
    {
      path: '/admin/system-prompts',
      element: withAppLayout(<SystemPromptsPage />)
    },
    {
      path: '/admin/model-management',
      element: withAppLayout(
        <OnlyInSingleUserMode>
          <ModelManagementPage />
        </OnlyInSingleUserMode>
      )
    },
    {
      // 資料匯出（知識庫原始檔 + 對話紀錄）：目前只做線上版，單機版整頁隱藏
      path: '/export',
      // 2026-09-29 起單機版亦開放（v1.2.0）：後端守衛以本機 super_admin 身分自然放行，
      // 對話以本機使用者過濾；下載走 window.location.assign，Electron 跳原生存檔對話框。
      element: withAppLayout(<ExportPage />)
    },
    {
      path: '/env',
      element: <Env />
    },
    {
      path: '/thread/:id?',
      element: withAppLayout(<Thread />)
    },
    {
      path: '/element/:id',
      element: <Element />
    },
    {
      path: '/login',
      element: <Login />
    },
    {
      path: '/login/callback',
      element: <AuthCallback />
    },
    {
      path: '/share/:id',
      element: <Thread />
    },
    {
      path: '*',
      element: <Navigate replace to="/chat" />
    }
  ],
  { basename: getRouterBasename() }
);
